import { useMutation, useQuery } from "@apollo/client";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { AdhocReportBody } from "../../../components/Modals/AdhocReport";
import ExportActiveReport from "../../../components/Modals/ExportActiveReport";
import { Breadcrumb, Button, MenuButton, Modal } from "../../../components/ui";
import { DELETE_CONTACT } from "../../../graphql/mutation/Contact";
import {
  FETCH_CONTACT as fetchContact,
  FETCH_CONTACTS_SEARCH_SYNOPSIS,
} from "../../../graphql/query/Contact";
import useUser from "../../../hooks/useUser";
import { clearSelectionState } from "../../../util/clearSelectionState";
import { scopeBulkActionTargets } from "../../../util/scopeBulkActionTargets";
import routes from "../pages/Wizard/routes";
import { ExportContactsBody } from "./Actions/ExportContacts";
import { ExportEmailsBody } from "./Actions/ExportEmails";
import exportModule from "./Actions/ExportModule";
import { GenerateReportBody } from "./Actions/GenerateReport";
import { HandoverBody } from "./Actions/Handover";
import MergeContact from "./Actions/MergeContact";
import NewContact from "./Actions/NewContact";
import SubscribeModal from "./Actions/Subscribe";
import UpdateCRM from "./Actions/UpdateCRM";
import ContactSearch from "./ContactSearch";
import { getContactSearchFromLocationSearch } from "./useContactFilters";

const SALESFORCE_BASE_URL =
  process.env.REACT_APP_SENTRY_ENV === "production"
    ? "https://white-cube.lightning.force.com"
    : "https://white-cube--uatwc.sandbox.lightning.force.com";


const PrimaryNav = props => {
  const { formik } = props;
  const selected = Object.keys(formik?.values?.selected?.ids || {});
  const ids = selected?.map(key => {
    const obj = formik?.values?.selected?.ids?.[key];
    return obj?.item?.id || obj?.id;
  });
  const select = formik?.values?.selected?.select;

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isHandoverOpen, setIsHandoverOpen] = useState(false);
  const [isExportReportOpen, setIsExportReportOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const location = useLocation();
  const search = getContactSearchFromLocationSearch(location.search);
  const navigate = useNavigate();

  const hasSearch =
    search != null &&
    typeof search === "object" &&
    !Array.isArray(search) &&
    Object.keys(search).length > 0;
  const { data: { contactsV2: { searchSynopsis } = {} } = {} } = useQuery(
    FETCH_CONTACTS_SEARCH_SYNOPSIS,
    {
      skip: !hasSearch,
      variables: { input: { ...(search || {}), limit: 1 } },
      fetchPolicy: "cache-first",
    },
  );
  const { hasPermission } = useUser();
  const pathnames = location?.pathname.split("/");
  // get id from url
  const contactId = pathnames?.length > 2 ? pathnames[2] : null;
  // fetch contact details if id is available
  const { data: { contact: fetchedContact } = {} } = useQuery(
    fetchContact({
      name: true,
      salesForceId: true,
      salesUserId: true,
    }),
    {
      skip: !contactId,
      variables: { id: +contactId },
    },
  );
  const [
    deleteContact,
    {
      data: { deleteContact: { error: deleteContactError } = {} } = {},
      loading: deleteContactLoading,
      reset,
    },
  ] = useMutation(DELETE_CONTACT);
  const salesforceContactUrl = fetchedContact?.salesForceId
    ? `${SALESFORCE_BASE_URL}/lightning/r/Account/${fetchedContact.salesForceId}/view`
    : null;

  // find current submodule route
  const path = pathnames[pathnames?.length - 1];
  const route = routes?.find(item => item?.href?.includes(path));
  const queryParams = new URLSearchParams(window.location.search);
  const status = queryParams.get("status") || "all";
  const listId = +(
    queryParams.get("listId") ||
    (status === "selectListWorking" ? queryParams.get("workingListId") : null)
  );

  const STATUS_MAP = {
    all: "All Contacts",
    my: "My Contacts",
    searched: "Searched Contacts",
    selectListWorking: "Select List (Working)",
  };
  const statusLabel = STATUS_MAP[status] || "All Contacts";
  // The breadcrumb root usually shows the active list ("All Contacts" /
  // "My Contacts" / …), but if the user has an active Contact Search
  // we substitute the human-readable synopsis there so the page makes
  // it obvious *what* they're looking at. We add a bold "Contact
  // Search" prefix here (rather than baking it into the API string)
  // so the framing is styled, not literal text. The label is rendered
  // in `normalCase` so connectors like "between" / "ordered by" stay
  // lowercase and are allowed to wrap freely instead of being cut off
  // with an ellipsis. It's also marked `disabled` because clicking
  // the synopsis would only navigate back to the (unfiltered) list,
  // which isn't the affordance the text suggests — so the step reads
  // as a heading rather than a link.
  const rootLabel = searchSynopsis
    ? {
        label: (
          <>
            <span className="font-bold">Contact Search</span> {searchSynopsis}
          </>
        ),
        title: `Contact Search ${searchSynopsis}`,
        normalCase: true,
        disabled: true,
      }
    : statusLabel;
  const steps = [
    { label: rootLabel, onClick: () => navigate(`/contacts?status=${status}`) },
    ...(fetchedContact
      ? [
          {
            label: fetchedContact?.name,
            onClick: () => navigate(`${fetchedContact?.id}/details`),
          },
          {
            label: route?.name,
            onClick: () => navigate(`${fetchedContact?.id}/${route?.href}`),
          },
        ]
      : []),
  ];

  const breadcrumbProps = {
    currentStepIndex: steps?.length - 1,
    onChange: index => steps[index]?.onClick(),
    steps: steps?.map(item => item?.label),
  };

  const modalProps = {
    body: ContactSearch,
    closeOnBackdrop: true,
    scale: "fullscreen",
    isSearchOpen,
    onClose: () => setIsSearchOpen(false),
    onSearchSubmitted: () => clearSelectionState(formik),
  };

  const deleteModalProps = {
    title: deleteContactError ? "Error" : "Are you sure?",
    scale: "sm",
    description: deleteContactError
      ? deleteContactError
      : `You are about to permeantly delete the contact record for ${fetchedContact?.name}.`,
    closeOnBackdrop: true,
    body: ({ closeModal }) => {
      const handleDelete = () => {
        deleteContact({
          variables: {
            input: { id: fetchedContact?.id },
          },
          update: (cache, { data }) => {
            const { deleteContact: { success } = {} } = data || {};
            if (success) {
              closeModal();
              cache.evict({
                id: cache.identify({
                  __typename: "Contact",
                  id: contactId,
                }),
              });
              navigate("/contacts");
            }
          },
        });
      };

      return (
        <div className="mt-4 flex">
          <Button
            className="mr-3"
            label={deleteContactError ? "Close" : "Cancel"}
            action={deleteContactError ? "primary" : "default"}
            onClick={() => {
              closeModal?.();
              reset();
            }}
          />
          {!deleteContactError && (
            <Button
              label={deleteContactLoading ? "Deleting" : "Delete"}
              disabled={deleteContactLoading}
              onClick={handleDelete}
            />
          )}
        </div>
      );
    },
  };

  const scopedTargets = scopeBulkActionTargets({
    entityId: contactId,
    selectedIds: ids,
    fallbacks: { listId, search, select },
  });

  const defaultActionProps = {
    closeOnBackdrop: false,
    hideCloseButton: true,
    ids: scopedTargets.ids,
    listId: scopedTargets.listId,
    onClose: () => {
      formik?.resetForm();
    },
    scale: "md",
    search: scopedTargets.search,
    select: scopedTargets.select,
  };

  const actionMenuProps = {
    label: "Actions",
    options: [
      {
        label: "New contact",
        modalProps: {
          body: NewContact,
          ...defaultActionProps,
          isCreateOpen,
          onOpen: () => {
            setIsCreateOpen(true);
          },
          onClose: () => {
            setIsCreateOpen(false);
          },
        },
      },
      {
        disabled: !ids.length && !contactId,
        label: "Update CRM Info",
        modalProps: {
          body: UpdateCRM,
          ...defaultActionProps,
        },
      },
      {
        disabled:
          (!ids.length && !contactId) || !hasPermission("HANDOVER_CONTACT"),
        label: "Handover",
        modalProps: {
          body: HandoverBody,
          ...defaultActionProps,
          isHandoverOpen,
          onOpen: () => {
            setIsHandoverOpen(true);
          },
          onClose: () => {
            setIsHandoverOpen(false);
          },
        },
      },
      {
        disabled: !ids.length && !contactId,
        label: "Create an Adhoc Report",
        modalProps: {
          body: AdhocReportBody,
          ...defaultActionProps,
          exportModule,
        },
      },
      {
        disabled: !ids.length && !contactId,
        label: "Export Contact Report",
        modalProps: {
          ...defaultActionProps,
          body: ExportActiveReport,
          optimise: true,
          type: "Contact",
          isExportReportOpen,
          onOpen: () => {
            setIsExportReportOpen(true);
          },
          onClose: () => {
            setIsExportReportOpen(false);
          },
        },
      },
      {
        disabled: !ids.length && !contactId,
        label: (
          <>
            New Reporting{" "}
            <span className={`ml-1 inline-flex items-center rounded-full px-1.5 text-[8px] font-semibold leading-4 ${!ids.length && !contactId ? "bg-gray-100 text-gray-400" : "bg-green-100 text-green-700"}`}>
              BETA
            </span>
          </>
        ),
        modalProps: {
          ...defaultActionProps,
          body: GenerateReportBody,
          type: "Contact",
          onClose: () => {},
        },
      },
      {
        disabled: !ids.length && !contactId,
        label: "Export Emails",
        modalProps: {
          body: ExportEmailsBody,
          ...defaultActionProps,
        },
      },
      {
        disabled: !ids.length && !contactId,
        label: "Export Contacts",
        modalProps: {
          body: ExportContactsBody,
          ...defaultActionProps,
        },
      },
      {
        disabled: !ids.length && !contactId,
        label: "Subscribe",
        modalProps: {
          body: SubscribeModal,
          ...defaultActionProps,
        },
      },
      ...(contactId
        ? [
            {
              label: "Open in Salesforce",
              disabled: !fetchedContact?.salesForceId,
              href: salesforceContactUrl,
              target: "_blank",
              rel: "noopener,noreferrer",
            },
            {
              disabled: !hasPermission("DELETE_CONTACT"),
              label: "Delete contact",
              modalProps: deleteModalProps,
            },
            {
              label: "Merge contact",
              modalProps: {
                body: MergeContact,
                ...defaultActionProps,
                contact: fetchedContact,
              },
            },
          ]
        : []),
    ],
  };

  return (
    <div className="flex items-center justify-between gap-4 border-b px-8 py-5">
      {/*
        `min-w-0` lets the breadcrumb shrink inside the flex row so a
        long synopsis wraps onto its own line(s) instead of pushing
        the right-hand actions cluster off-screen. `flex-1` claims any
        leftover horizontal space.
      */}
      <div className="min-w-0 flex-1">
        <Breadcrumb {...breadcrumbProps} />
      </div>
      <div className="flex flex-shrink-0 justify-end gap-4">
        <Modal {...modalProps}>
          <Button
            label="Contact Search"
            action="black"
            onClick={() => setIsSearchOpen(true)}
          />
        </Modal>
        <MenuButton {...actionMenuProps} />
      </div>
    </div>
  );
};

export default PrimaryNav;
